import { Container, Stack } from '@mui/material';
import { type ReactNode } from 'react';
import ActiveOrganizationMenu from '../organizations/active-organization-menu';
import DashboardButton from '../common/dashboard-button';
import AdminMenu from '../common/admin-menu';
import { useActiveOrganizationStore } from '@/state/stores/use-organization-store';
import { useAuth } from '@clerk/clerk-react';
import { useGetUserQuery, isGlobalAdminUser } from '@/state/queries/users/use-get-user';

interface OrganizationContentStackProps {
  content: ReactNode;
}

export default function OrganizationContentStack({ content }: OrganizationContentStackProps) {
  const { isSignedIn } = useAuth();
  const { activeOrganization } = useActiveOrganizationStore();
  const hasActiveOrganization = !!activeOrganization;
  const { data: user } = useGetUserQuery();
  const isAdmin = Boolean(isSignedIn) && isGlobalAdminUser(user);

  return (
    <Container
      maxWidth={false}
      sx={{
        pt: { xs: 2, md: 1 },
        mt: { xs: '-8px', md: '8px' },
        px: { xs: 1, sm: 1, md: 3 },
        width: '100%',
        height: 'calc(100vh - 64px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        gap={2}
        sx={{
          alignItems: 'stretch',
          position: 'relative',
          width: '100%',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {(hasActiveOrganization || isAdmin) && (
          <Stack
            direction="column"
            gap={1.5}
            sx={{
              'zIndex': 2,
              'display': { xs: 'none', md: 'flex' },
              'flexShrink': 0,
              'width': 250,
              'height': '100%',
              'minHeight': 0,
              'overflowY': 'auto',
              'WebkitOverflowScrolling': 'touch',
              '& > *': {
                flexShrink: 0,
              },
            }}
          >
            {/* Dashboard Button */}
            <DashboardButton />

            {hasActiveOrganization && <ActiveOrganizationMenu />}

            {/* Admin Menu - visually separated */}
            {isAdmin && <AdminMenu />}
          </Stack>
        )}

        <Stack
          direction="column"
          gap={0}
          sx={{
            position: 'relative',
            zIndex: 1,
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            height: '100%',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {content}
        </Stack>
      </Stack>
    </Container>
  );
}
